import { type RefObject, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleCheck, Inbox, ListFilter, RefreshCw, TriangleAlert } from 'lucide-react';

import { Button } from '@/components/Button/Button';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { Notice, notify } from '@/components/Notice/Notice';
import {
    SegmentedControl,
    SegmentedControlItem,
} from '@/components/SegmentedControl/SegmentedControl';
import { Skeleton } from '@/components/Skeleton/Skeleton';
import { ReportCard, ReportFailure } from '@/features/reports/parts';
import {
    XERO_SYNC_CHECKS,
    XERO_SYNC_RUN_STATUS,
    XERO_SYNC_STATE,
    type XeroSync,
    type XeroSyncCheck,
    type XeroSyncFinding,
    type XeroSyncState,
    xeroSyncApi,
} from '@/features/reports/xero/api';
import { XERO_SYNC_COPY as COPY } from '@/features/reports/xero/copy';
import { DismissDialog } from '@/features/reports/xero/DismissDialog';
import { FindingList } from '@/features/reports/xero/FindingList';
import { whenText } from '@/features/reports/xero/labels';
import { xeroSyncKeys, xeroSyncQuery } from '@/features/reports/xero/queries';
import { XERO_STATE } from '@/features/xero/api';
import { xeroKeys } from '@/features/xero/queries';
import { useXeroConnection } from '@/features/xero/useXero';
import { ApiError } from '@/lib/api';
import { cn } from '@/util/utils';

/**
 * Reports › Xero sync (ALS-388): where the admin and Xero disagree, as the daily check found it.
 * When it last checked and what it found, Check now, one tile per check that narrows the list to it,
 * and the differences, open or dismissed. Nothing here changes either side: a difference is put right
 * in Xero or here by a person, and the next check finds it gone. While Xero is not connected the
 * check does not run and the tab says so, keeping what the last check found.
 *
 * <p>No prototype screen exists for it. It is composed from the Reports tabs' own parts: the order
 * queue's tiles as filters, the report card, and the register table that stacks on a phone.
 */
export function XeroSyncPanel({
    check,
    state,
    onCheckChange,
    onStateChange,
}: {
    check: XeroSyncCheck | undefined;
    state: XeroSyncState;
    onCheckChange: (check: XeroSyncCheck | undefined) => void;
    onStateChange: (state: XeroSyncState) => void;
}) {
    const client = useQueryClient();
    const sync = useQuery(xeroSyncQuery({ check, state }));
    const connection = useXeroConnection();
    const connected =
        connection !== null &&
        connection.state !== XERO_STATE.notLinked &&
        connection.state !== XERO_STATE.disconnected;
    const data = sync.data;
    useAnnounceFinished(data);

    const [dismissing, setDismissing] = useState<XeroSyncFinding | null>(null);
    // What the dialog shows: the difference being dismissed, kept while the dialog closes.
    const [inDialog, setInDialog] = useState<XeroSyncFinding | null>(null);
    const [refused, setRefused] = useState<string | null>(null);
    const panel = useRef<HTMLDivElement>(null);
    const keyboard = useFocusHandover(data, panel);
    const refresh = (finding?: XeroSyncFinding) => {
        void client.invalidateQueries({ queryKey: xeroSyncKeys.all });
        // An order's "Differs in Xero" reads its dismissal, so the order page catches up too.
        if (finding?.order)
            void client.invalidateQueries({ queryKey: xeroKeys.order(finding.order.id) });
    };

    const run = useMutation({
        mutationFn: xeroSyncApi.run,
        onSuccess: () => {
            notify.info(COPY.status.started);
            refresh();
        },
        onError: (error) =>
            notify.error(COPY.status.startFailed, {
                description: error instanceof ApiError ? error.problem.detail : undefined,
            }),
    });
    const dismiss = useMutation({
        mutationFn: ({ finding, reason }: { finding: XeroSyncFinding; reason: string }) =>
            xeroSyncApi.dismiss(finding.id, reason, finding.figures),
        onSuccess: (_, { finding }) => {
            keyboard.leaving(finding);
            setDismissing(null);
            notify.success(COPY.dialog.done);
            refresh(finding);
        },
        onError: (error, { finding }) => {
            if (error instanceof ApiError && error.status === 409) {
                setRefused(error.problem.detail ?? COPY.dialog.failed);
                refresh(finding);
                return;
            }
            notify.error(COPY.dialog.failed, {
                description: error instanceof ApiError ? error.problem.detail : undefined,
            });
        },
    });
    const restore = useMutation({
        mutationFn: (finding: XeroSyncFinding) => xeroSyncApi.restore(finding.id),
        onSuccess: (_, finding) => {
            keyboard.leaving(finding);
            notify.success(COPY.restored);
            refresh(finding);
        },
        onError: (error, finding) => {
            notify.error(COPY.restoreFailed, {
                description: error instanceof ApiError ? error.problem.detail : undefined,
            });
            refresh(finding);
        },
    });

    if (sync.isPending) return <PanelSkeleton />;
    if (sync.isError && !data) {
        return <ReportFailure copy={COPY.failed} onRetry={() => void sync.refetch()} />;
    }
    if (!data) return null;

    const running = data.running !== null;
    const shown = data.findings;
    const totalOpen = XERO_SYNC_CHECKS.reduce((sum, c) => sum + data.open[c], 0);

    return (
        <div ref={panel} className="flex min-w-0 flex-col gap-(--layout-row-gap)">
            <StatusStrip
                sync={data}
                connected={connected}
                checking={running || run.isPending}
                onCheckNow={() => run.mutate()}
            />
            <CheckTiles open={data.open} selected={check} onSelect={onCheckChange} />
            <ReportCard
                title={COPY.card.title}
                subtitle={COPY.card.count(
                    state === XERO_SYNC_STATE.Open ? totalOpen : data.dismissed,
                )}
                actions={
                    <SegmentedControl
                        aria-label={COPY.card.showing}
                        value={state}
                        onValueChange={(next) => next && onStateChange(next as XeroSyncState)}
                    >
                        <SegmentedControlItem
                            value={XERO_SYNC_STATE.Open}
                            className="pointer-coarse:min-h-11"
                        >
                            {COPY.card.open(totalOpen)}
                        </SegmentedControlItem>
                        <SegmentedControlItem
                            value={XERO_SYNC_STATE.Dismissed}
                            className="pointer-coarse:min-h-11"
                        >
                            {COPY.card.dismissed(data.dismissed)}
                        </SegmentedControlItem>
                    </SegmentedControl>
                }
            >
                {shown.length > 0 ? (
                    <FindingList
                        findings={shown}
                        mayAct={data.mayAct}
                        busy={restore.isPending ? (restore.variables?.id ?? null) : null}
                        onDismiss={(finding, button) => {
                            keyboard.from(button);
                            setRefused(null);
                            setDismissing(finding);
                            setInDialog(finding);
                        }}
                        onRestore={(finding) => restore.mutate(finding)}
                    />
                ) : (
                    <Empty
                        sync={data}
                        connected={connected}
                        check={check}
                        state={state}
                        onShowAll={() => onCheckChange(undefined)}
                    />
                )}
            </ReportCard>
            <DismissDialog
                open={dismissing !== null}
                finding={inDialog ? (shown.find((f) => f.id === inDialog.id) ?? inDialog) : null}
                onOpenChange={(open) => {
                    if (!open) setDismissing(null);
                }}
                returnFocusTo={keyboard.opener}
                pending={dismiss.isPending}
                refused={refused}
                onConfirm={(reason) => {
                    if (!inDialog) return;
                    // The figures as the list holds them now: a refusal re-reads them before a second try.
                    const latest = shown.find((f) => f.id === inDialog.id) ?? inDialog;
                    setRefused(null);
                    dismiss.mutate({ finding: latest, reason });
                }}
            />
        </div>
    );
}

/**
 * When Xero was last checked by a run that finished and what it found, when it runs next, and Check
 * now. A run under way reads as checking, and the button waits for it; a run that could not finish
 * says why and when it tries again, and is never taken for a check. While Xero is not connected the
 * button is off, and why is said in words, not by its look.
 */
function StatusStrip({
    sync,
    connected,
    checking,
    onCheckNow,
}: {
    sync: XeroSync;
    connected: boolean;
    checking: boolean;
    onCheckNow: () => void;
}) {
    const last = sync.lastRun;
    const checked = sync.lastChecked;
    const failed = last?.status === XERO_SYNC_RUN_STATUS.Failed;
    return (
        <section
            aria-label={COPY.status.label}
            className="bg-card border-border flex min-w-0 flex-col gap-3 rounded-lg border px-4 py-3"
        >
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <div className="flex min-w-0 flex-col gap-0.5" aria-live="polite">
                    <p className="text-sm font-medium">
                        {checking
                            ? COPY.status.checking
                            : checked?.finishedAt
                              ? COPY.status.lastChecked(whenText(checked.finishedAt))
                              : COPY.status.neverChecked}
                    </p>
                    <p className="text-muted-foreground text-xs">
                        {[
                            checked && checked.found !== null
                                ? COPY.status.counts(checked.found, checked.new ?? 0)
                                : null,
                            connected ? COPY.status.next(whenText(sync.nextNightlyAt)) : null,
                        ]
                            .filter((part): part is string => part !== null)
                            .join(' · ')}
                    </p>
                </div>
                {sync.mayAct ? (
                    <Button
                        variant="outline"
                        size="sm"
                        className="pointer-coarse:h-11"
                        disabled={!connected || checking}
                        loading={checking}
                        aria-label={COPY.status.checkNowLabel}
                        onClick={onCheckNow}
                    >
                        <RefreshCw />
                        {COPY.status.checkNow}
                    </Button>
                ) : null}
            </div>
            {!connected ? (
                <Notice tone="warning" icon={<TriangleAlert />}>
                    {COPY.status.disconnected}
                </Notice>
            ) : failed && !checking ? (
                <Notice tone="warning" icon={<TriangleAlert />}>
                    {COPY.status.failed(last.problem, last.retryAt ? whenText(last.retryAt) : null)}
                </Notice>
            ) : null}
        </section>
    );
}

/** One tile per check with what it has open; pressing one narrows the list to it, pressing it again shows every check. */
function CheckTiles({
    open,
    selected,
    onSelect,
}: {
    open: Record<XeroSyncCheck, number>;
    selected: XeroSyncCheck | undefined;
    onSelect: (check: XeroSyncCheck | undefined) => void;
}) {
    return (
        <div
            role="group"
            aria-label={COPY.tiles}
            className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-7"
        >
            {XERO_SYNC_CHECKS.map((check) => {
                const on = selected === check;
                const label = COPY.checks[check];
                return (
                    <button
                        key={check}
                        type="button"
                        aria-pressed={on}
                        aria-label={COPY.tileCount(label, open[check])}
                        onClick={() => onSelect(on ? undefined : check)}
                        className={cn(
                            'bg-card border-border min-h-11 min-w-0 rounded-lg border px-4 py-3 text-left',
                            'focus-visible:outline-ring outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid',
                            on && 'border-primary ring-primary ring-1',
                        )}
                    >
                        <span
                            className={cn(
                                'block text-2xl font-semibold tracking-tight',
                                open[check] === 0 && 'text-muted-foreground',
                            )}
                        >
                            {open[check]}
                        </span>
                        <span className="text-label-md text-muted-foreground mt-0.5 block truncate">
                            {label}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

/**
 * What the list says when it has nothing to show: nothing dismissed, no check finished yet, nothing
 * under one check, or all agreeing. Only a check that finished can say the two agree: before one has,
 * an empty list means nothing was compared.
 */
function Empty({
    sync,
    connected,
    check,
    state,
    onShowAll,
}: {
    sync: XeroSync;
    connected: boolean;
    check: XeroSyncCheck | undefined;
    state: XeroSyncState;
    onShowAll: () => void;
}) {
    if (state === XERO_SYNC_STATE.Dismissed) {
        return (
            <EmptyState
                icon={<Inbox />}
                title={COPY.empty.dismissed.title}
                body={COPY.empty.dismissed.body}
            />
        );
    }
    const checked = sync.lastChecked;
    if (!checked) {
        return (
            <EmptyState
                icon={<RefreshCw />}
                title={COPY.empty.never.title}
                body={
                    !connected
                        ? COPY.empty.never.disconnected
                        : sync.lastRun?.status === XERO_SYNC_RUN_STATUS.Failed
                          ? COPY.empty.never.failed
                          : COPY.empty.never.body(whenText(sync.nextNightlyAt))
                }
            />
        );
    }
    if (check) {
        return (
            <EmptyState
                icon={<ListFilter />}
                title={COPY.empty.check.title(COPY.checks[check])}
                body={COPY.empty.check.body}
                actions={
                    <Button variant="outline" size="sm" onClick={onShowAll}>
                        {COPY.empty.check.action}
                    </Button>
                }
            />
        );
    }
    return (
        <EmptyState
            tone="success"
            icon={<CircleCheck />}
            title={COPY.empty.agree.title}
            body={
                checked.finishedAt ? COPY.empty.agree.body(whenText(checked.finishedAt)) : undefined
            }
        />
    );
}

/**
 * Where the keyboard goes when the row it acted from leaves the list (dismissed from Open, restored
 * from Dismissed): to the row that takes its place, else the one before, else the Open/Dismissed
 * control, as the mobile contract asks of "a sensible surviving record". The dialog's own return,
 * if it is still closing, is pointed there too, so whichever finishes last lands in the same place.
 * It keeps the button an action was taken from, and is told when that action took a finding away.
 */
function useFocusHandover(sync: XeroSync | undefined, panel: RefObject<HTMLDivElement | null>) {
    const opener = useRef<HTMLElement | null>(null);
    const plan = useRef<{ leaving: number; next: number | null } | null>(null);
    useEffect(() => {
        const pending = plan.current;
        if (!pending || !sync || sync.findings.some((f) => f.id === pending.leaving)) return;
        plan.current = null;
        const root = panel.current;
        const target =
            (pending.next !== null
                ? root?.querySelector<HTMLElement>(`[data-finding-action="${pending.next}"]`)
                : null) ??
            root?.querySelector<HTMLElement>('[data-slot="segmented-control"] [data-state="on"]');
        if (!target) return;
        opener.current = target;
        target.focus();
    }, [sync, panel]);

    return {
        /** Where the dismiss dialog hands the keyboard back when it closes. */
        opener,
        /** The button an action was taken from. */
        from: (button: HTMLElement) => {
            opener.current = button;
        },
        /** The finding an action has just taken off the list. */
        leaving: (finding: XeroSyncFinding) => {
            const list = sync?.findings ?? [];
            const at = list.findIndex((f) => f.id === finding.id);
            const next = list[at + 1] ?? list[at - 1] ?? null;
            plan.current = { leaving: finding.id, next: next?.id ?? null };
        },
    };
}

/**
 * Says so when a run that was under way ends while the tab is open: what it found, or that it could
 * not finish. Once per run, keyed on the run that was under way.
 */
function useAnnounceFinished(sync: XeroSync | undefined) {
    const watching = useRef<number | null>(null);
    useEffect(() => {
        if (!sync) return;
        const was = watching.current;
        watching.current = sync.running?.id ?? null;
        if (was === null || sync.running !== null || sync.lastRun?.id !== was) return;
        if (sync.lastRun.status === XERO_SYNC_RUN_STATUS.Done) {
            notify.success(COPY.status.finished(sync.lastRun.new ?? 0));
        } else {
            notify.warning(COPY.status.failed(sync.lastRun.problem, null));
        }
    }, [sync]);
}

function PanelSkeleton() {
    return (
        <div
            aria-busy="true"
            aria-label="Loading the Xero sync"
            className="flex flex-col gap-(--layout-row-gap)"
        >
            <Skeleton className="h-[68px] rounded-lg" />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-7">
                {XERO_SYNC_CHECKS.map((check) => (
                    <Skeleton key={check} className="h-[76px] rounded-lg" />
                ))}
            </div>
            <Skeleton className="h-[320px] rounded-md" />
        </div>
    );
}
